import { useMemo } from 'react';

import type { Competitor } from './useCompetitors';
import {
  type PostingRoleFilters,
  usePostingsComparison,
} from './usePostingsComparison';
import {
  buildCustomRoleFilter,
  type ResolvedProfileIds,
  type RoleLevel,
} from './useResolveProfileIds';

export interface CompanyLeverageSignal {
  companyId: number;
  companyName: string;
  postingSalary: number | null;
  marketMedian: number | null;
  salaryDeltaPct: number | null;
  timeToFill: number | null;
  activePostings: number | null;
  leverageLevel: 'high' | 'medium' | 'low' | null;
}

interface UseRolePostingSignalsResult {
  signals: CompanyLeverageSignal[];
  roleName: string | null;
  isLoading: boolean;
  error: string | null;
}

const computeLeverage = (
  timeToFill: number | null,
  activePostings: number | null
): 'high' | 'medium' | 'low' | null => {
  if (timeToFill === null && activePostings === null) return null;
  if (
    (timeToFill !== null && timeToFill > 45) ||
    (activePostings !== null && activePostings > 10)
  ) {
    return 'high';
  }
  if (
    (timeToFill !== null && timeToFill > 30) ||
    (activePostings !== null && activePostings > 5)
  ) {
    return 'medium';
  }
  return 'low';
};

/**
 * Pick the best posting-compatible role level from the resolved role levels.
 * The postings API supports role_k150 and role_k1500.
 * Prefer role_k1500 (more specific) if available, then role_k150.
 */
const pickPostingRoleLevel = (roleLevels: RoleLevel[]): RoleLevel | null => {
  const k1500 = roleLevels.find((l) => l.type === 'role_k1500');
  if (k1500) return k1500;
  const k150 = roleLevels.find((l) => l.type === 'role_k150');
  if (k150) return k150;
  return null;
};

/**
 * Fetch role-specific posting signals for a set of companies.
 *
 * @param competitors - competitor companies to include
 * @param resolvedIds - resolved profile IDs (companyRcid used as primary)
 * @param roleLevels - all resolved role levels (picks best posting-compatible one)
 * @param seniorityId - seniority ID to filter by (null = no seniority filter)
 * @param additionalCompanyIds - extra company IDs (e.g. overlay/historical)
 * @param marketMedian - median salary from the pay distribution (for delta calc)
 */
export const useRolePostingSignals = (
  competitors: Competitor[],
  resolvedIds: ResolvedProfileIds,
  roleLevels: RoleLevel[],
  seniorityId: number | null,
  additionalCompanyIds: number[],
  marketMedian: number | null
): UseRolePostingSignalsResult => {
  const bestRole = useMemo(
    () => pickPostingRoleLevel(roleLevels),
    [roleLevels]
  );

  const roleFilters = useMemo((): PostingRoleFilters | undefined => {
    if (!bestRole) return undefined;

    const filters: PostingRoleFilters = {
      custom_role: buildCustomRoleFilter(bestRole),
    };
    if (seniorityId) {
      filters.seniority = seniorityId;
    }

    return filters;
  }, [bestRole, seniorityId]);

  const { postings, isLoading, error } = usePostingsComparison(
    competitors,
    resolvedIds,
    additionalCompanyIds,
    roleFilters
  );

  const signals = useMemo((): CompanyLeverageSignal[] => {
    return postings.map((p) => {
      let salaryDeltaPct: number | null = null;
      if (
        p.postingSalary !== null &&
        marketMedian !== null &&
        marketMedian > 0
      ) {
        salaryDeltaPct =
          ((p.postingSalary - marketMedian) / marketMedian) * 100;
      }

      return {
        companyId: p.companyId,
        companyName: p.shortName,
        postingSalary: p.postingSalary,
        marketMedian,
        salaryDeltaPct,
        timeToFill: p.timeToFill,
        activePostings: p.activePostings,
        leverageLevel: computeLeverage(p.timeToFill, p.activePostings),
      };
    });
  }, [postings, marketMedian]);

  return { signals, roleName: bestRole?.name ?? null, isLoading, error };
};
